'use client';

import {
  type ProductVariant,
  type CartLineInput,
  type CartInput,
} from '@shopify/hydrogen-react/storefront-api-types';
import { useCallback, useState } from 'react';

import { DATA_KEYS } from '@/consts/dataKeys';
import { useRouter } from '@/hooks/useRouter';
import { useVisitContext } from '@/services/visitsService/hooks/useVisitContext';
import { client } from '@/utils/shopify/client';
import { urls } from '@/utils/urls';

import { type ShopifyCartProduct } from './types';
import { useDataProvider } from '../../components/DataProvider/useDataProvider';

const createCartMutation = `
    mutation ($input: CartInput!) {
      cartCreate(input: $input) {
        userErrors {
          message
          code
          field
        }
        cart {
          id
          checkoutUrl
        }
      }
    }
  `;

const useShopifyCart = ({
  defaultVariants,
  discountCodes,
}: {
  defaultVariants: string[];
  discountCodes: string[];
}) => {
  const [lines, setLines] = useState<CartLineInput[]>(
    defaultVariants.map((variant) => ({
      merchandiseId: variant,
      quantity: 1,
    })),
  );
  const router = useRouter();
  const { visit } = useVisitContext();
  const patient = useDataProvider(DATA_KEYS.basicPatient);

  const isSelected = useCallback(
    (product: ShopifyCartProduct) =>
      lines.some((line) =>
        product.variants.nodes
          .map((variant) => variant.id)
          .includes(line.merchandiseId),
      ),
    [lines],
  );

  const isVariantSelected = useCallback(
    (productVariantId: string) =>
      lines.some((line) => line.merchandiseId === productVariantId),
    [lines],
  );

  const toggle = useCallback(
    (variant: Pick<ProductVariant, 'id'>) => {
      setLines((prevLines) => {
        const newLines = prevLines.filter(
          (line) => variant.id !== line.merchandiseId,
        );

        if (newLines.length === prevLines.length) {
          newLines.push({
            merchandiseId: variant.id,
            quantity: 1,
          });
        }

        return newLines;
      });
    },
    [setLines],
  );

  const submit = useCallback(async () => {
    if (!visit) {
      throw new Error('Visit not found');
    }

    const visitUrl = urls[visit.brand].base + urls.app.visit.flow(visit.uuid);

    const { data, errors } = await client.request(createCartMutation, {
      variables: {
        input: {
          lines,
          attributes: [
            { key: 'visitId', value: visit.uuid },
            { key: 'visitUrl', value: visitUrl },
          ],
          discountCodes,
          buyerIdentity: {
            email: patient?.email,
          },
        } satisfies CartInput,
      },
    });

    if (errors?.graphQLErrors) {
      throw new Error(errors.graphQLErrors[0].message);
    }

    router.push(data.cartCreate.cart.checkoutUrl);
  }, [router, lines, discountCodes]);

  return {
    isSelected,
    isVariantSelected,
    toggle,
    submit,
  };
};

export { useShopifyCart };
